/*
 * home (page)
 */
import { appConfig } from "@psychology/config"
import {
	Badge,
	ButtonLink,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
} from "@psychology/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { BookOpen, Bot, ShieldCheck } from "lucide-react"
import { authPageLoaderSoft } from "../utils/auth.utils"

export const Route = createFileRoute("/")({
	component: HomePage,
	loader: authPageLoaderSoft({
		allowUnauthenticated: true,
		redirectAuthorized: appConfig.redirects.afterSignInUrl,
	}),
})

const features = [
	{
		icon: Bot,
		title: "AI Support, Always On",
		description:
			"Talk to an AI that actually gets it — whether you're tilted after a loss, grinding through burnout, or just need to vent. Available 24/7, no judgment.",
	},
	{
		icon: BookOpen,
		title: "Journal Your Journey",
		description:
			"Log your thoughts, track your mood, and let AI surface patterns you might miss. Build self-awareness between sessions and beyond the screen.",
	},
	{
		icon: ShieldCheck,
		title: "Tools Built for Wellbeing",
		description:
			"Track traits, run mental health check-ins, manage your support network, and generate documents for appointments — all in one place.",
	},
]

function HomePage() {
	const { authenticated } = Route.useLoaderData()
	const waitlistHref = authenticated
		? "/waitlist"
		: "/auth?tab=register&waitlist=true"

	return (
		<Page centerVertical={false}>
			<Container width={"xxxl"} padding={"lg"} gap={"lg"}>
				{/* Hero */}
				<section
					className={
						"w-full flex flex-col items-center py-24 px-4 gap-6 text-center"
					}
				>
					<Badge variant={"outline"} className={"text-sm px-4 py-1"}>
						Early Access
					</Badge>
					<h1 className={"text-5xl font-bold tracking-tight max-w-2xl"}>
						Your mental game matters.
					</h1>
					<p className={"text-muted-foreground text-lg max-w-xl"}>
						Built for gamers who know performance starts with mental health.
						AI-powered tools to help you journal, reflect, and stay on top of
						your wellbeing — on your terms.
					</p>
					<div className={"flex flex-col sm:flex-row gap-3 mt-2"}>
						<ButtonLink to={waitlistHref} size={"lg"}>
							Get Early Access
						</ButtonLink>
					</div>
				</section>
				<section className={"w-full flex justify-center px-4 pb-24"}>
					<div className={"grid grid-cols-1 sm:grid-cols-3 gap-6"}>
						{features.map(({ icon: Icon, title, description }) => (
							<Card key={title} className={"bg-background/60 backdrop-blur-sm"}>
								<CardHeader className={"gap-3"}>
									<div
										className={
											"w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"
										}
									>
										<Icon className={"w-5 h-5 text-primary"} />
									</div>
									<CardTitle className={"text-base"}>{title}</CardTitle>
								</CardHeader>
								<CardContent>
									<p className={"text-sm text-muted-foreground"}>
										{description}
									</p>
								</CardContent>
							</Card>
						))}
					</div>
				</section>
			</Container>
		</Page>
	)
}
